import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Collapse,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import logoEuroansa from '../assets/images/Logo_euroansa_COMPLETO_BLU.png';

const drawerWidth = 250;

interface MenuItem {
  id: string;
  name: string;
  path: string | null;
  renderIcon: () => JSX.Element;
  subItems?: MenuItem[];
  activePaths?: string[];
  position?: string;
}

const isPathActive = (url: string, path: string | null | undefined) => {
  if (!path) return false;
  return url === path || url.startsWith(path + '/');
};

const isMenuItemActive = (url: string, menuItem: MenuItem) =>
  isPathActive(url, menuItem.path) ||
  menuItem.activePaths?.some((path) => isPathActive(url, path)) ||
  menuItem.subItems?.some((subItem) => isPathActive(url, subItem.path)) ||
  false;

const NavBarLayout = ({
  children,
  menuOptions,
  logo,
  onLogoutPress,
  appVersion,
  renderCustomContent,
}: {
  children: React.ReactNode;
  logo: string;
  onLogoutPress?: () => void;
  appVersion?: string;
  menuOptions: MenuItem[];
  renderCustomContent?: React.ReactNode;
}) => {
  const location = useLocation();
  const url = location.pathname;

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [subItemOpen, setSubItemOpen] = useState<string | null>(null);

  useEffect(() => {
    if (url) {
      const findUrlInSubMenuOption = menuOptions.find(
        (option) => option.subItems && isMenuItemActive(url, option)
      );
      if (findUrlInSubMenuOption) {
        setSubItemOpen(findUrlInSubMenuOption.id);
      }
    }
  }, [menuOptions, url]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const MenuItemOption = ({ op }: { op: MenuItem }) => (
    <Box>
      {op.path && !op.subItems && (
        <Link
          to={op.path}
          aria-current={isPathActive(url, op.path) ? 'page' : undefined}
          style={{
            textDecoration: 'none',
          }}
        >
          <ListItem
            sx={{
              background: (theme) =>
                isPathActive(url, op.path)
                  ? 'white'
                  : theme.palette.primary.main,
              color: (theme) =>
                isPathActive(url, op.path)
                  ? theme.palette.text.primary
                  : 'white',
              py: 1,
              borderRadius: 1,
              boxShadow: 'inherit',
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              {op.renderIcon()}
            </ListItemIcon>
            <ListItemText primary={op.name} />
          </ListItem>
        </Link>
      )}
      {op.subItems && (
        <>
          <ListItemButton
            aria-current={isMenuItemActive(url, op) ? 'location' : undefined}
            aria-expanded={subItemOpen === op.id}
            selected={isMenuItemActive(url, op)}
            onClick={() =>
              setSubItemOpen((openItemId) =>
                openItemId === op.id ? null : op.id
              )
            }
            sx={{
              borderRadius: 1,
              '&.Mui-selected': {
                backgroundColor: 'white',
                color: (theme) => theme.palette.text.primary,
              },
              '&.Mui-selected:hover': {
                backgroundColor: 'white',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              {op.renderIcon()}
            </ListItemIcon>
            <ListItemText primary={op.name} />
            {subItemOpen === op.id ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={subItemOpen === op.id} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {op.subItems.map((so) => (
                <Link
                  key={so.id}
                  to={so?.path || ''}
                  aria-current={isPathActive(url, so.path) ? 'page' : undefined}
                  style={{
                    textDecoration: 'none',
                  }}
                >
                  <ListItem
                    sx={{
                      pl: 4,
                      background: (theme) =>
                        isPathActive(url, so.path)
                          ? 'white'
                          : theme.palette.primary.main,
                      color: (theme) =>
                        isPathActive(url, so.path)
                          ? theme.palette.text.primary
                          : 'white',
                      py: 1,
                      borderRadius: 1,
                      boxShadow: 'inherit',
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit' }}>
                      {so.renderIcon()}
                    </ListItemIcon>
                    <ListItemText primary={so.name} />
                  </ListItem>
                </Link>
              ))}
            </List>
          </Collapse>
        </>
      )}
    </Box>
  );

  const drawer = (
    <Box
      sx={{
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          color: 'white',
          pt: '16px',
          borderWidth: 0,
          minHeight: '100%',
        }}
      >
        <Box sx={{ px: 0 }}>
          <Box
            sx={{
              display: 'flex',
              p: '16px',
              justifyContent: 'center',
            }}
          >
            <img src={logo} alt="" width="100%" />
          </Box>
          <List sx={{ pt: 2 }}>
            {menuOptions.map(
              (op) =>
                op?.position !== 'bottom' && (
                  <MenuItemOption key={op.id} op={op} />
                )
            )}
          </List>
        </Box>
        <Box>
          <Box
            sx={{
              textAlign: 'center',
              p: 2,
              bgcolor: '#f0f0f0',
              m: 2,
              borderRadius: '5px',
            }}
          >
            <img
              src={logoEuroansa}
              alt="Logo"
              width="100%"
              style={{ maxWidth: '80px' }}
            />
            <p
              style={{
                color: 'rgb(48 118 180)',
                fontWeight: 'bold',
                marginBottom: '0px',
              }}
            >
              Migliora la tua presenza digitale in pochi click
            </p>
            <p
              style={{
                color: '#103c55',
                fontWeight: 'bold',
                marginTop: '5px',
                marginBottom: '25px',
              }}
            >
              Scopri perchè la tua presenza digitale fa la differenza
            </p>
            <a
              href="https://consulenti.euroansa.it"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'white',
                textDecoration: 'none',
                backgroundColor: '#103c55',
                padding: '10px 20px',
                borderRadius: '5px',
                marginBottom: '25px',
              }}
            >
              {'Scopri come'}
            </a>
          </Box>

          {renderCustomContent}

          {menuOptions.map(
            (op) =>
              op?.position === 'bottom' && (
                <MenuItemOption key={op.id} op={op} />
              )
          )}
          {onLogoutPress && (
            <ListItem
              sx={{ color: 'white', cursor: 'pointer' }}
              onClick={onLogoutPress}
            >
              <ListItemIcon>
                <LogoutIcon sx={{ color: 'white' }}></LogoutIcon>
              </ListItemIcon>
              <ListItemText color="inherit" primary="Logout" />
            </ListItem>
          )}
          {appVersion && (
            <Typography px={2} pb={2} fontSize="12px" color="white">
              Versione {appVersion}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );

  const container = () => window.document.body;

  return (
    <Box
      sx={{
        display: 'flex',
        bgcolor: (theme) => theme.palette.background.default,
      }}
    >
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          bgcolor: (theme) => theme.palette.primary.main,
          // width: {sm: `calc(100% - ${drawerWidth}px)`},
          ml: {
            sm: `${drawerWidth}px`,
            md: `${drawerWidth}px`,
            lg: `${drawerWidth}px`,
            xl: `${drawerWidth}px`,
          },
          zIndex: 999,
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: { sm: 'none' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <div>
              <IconButton
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  mr: 2,
                  display: { sm: 'none' },
                  color: 'white',
                }}
              >
                <MenuIcon />
              </IconButton>
            </div>
            {/* <IconButton
                            sx={{
                                backgroundColor: (theme) => theme.palette.primary.light,
                                p: 0,
                                width: '50px',
                                height: '50px',
                            }}
                        >
                            <Link href={'/logout'} style={{ color: 'white', padding: 0 }}>
                                <LogoutIcon />
                            </Link>
                        </IconButton>*/}
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth },
          flexShrink: { sm: 0 },
          // minHeight: '100vh',
        }}
        className="no-print"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            minHeight: '100vh',
            display: {
              xs: 'block',
              sm: 'none',
            },
            '& .MuiDrawer-paper': {
              bgcolor: (theme) => theme.palette.primary.main,
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            //minHeight: '100vh',
            display: {
              xs: 'none',
              sm: 'block',
            },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              //borderRightWidth: 0,
              overflowX: 'hidden',
              zIndex: 99,
              bgcolor: (theme) => theme.palette.primary.main,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          //px: 2,
          width: {
            sm: `calc(100% - ${drawerWidth}px)`,
          },
        }}
      >
        <Toolbar
          sx={{
            display: {
              xs: 'block',
              sm: 'none',
            },
          }}
        />

        {children}
      </Box>
    </Box>
  );
};

export default NavBarLayout;
